/**
 * Der Reiter „Historie" des Bestell-Drawers - neuer Reiter (kein Bestandsvorbild in `index.html`,
 * Auftrag "Bestellungs-Aenderungshistorie", Task 11 - Frontend-Gegenstueck zu Task 10s vereinheitlichtem
 * Zeitstrahl `GET /api/orders/:id/history`, `backend/src/domaene/historie.ts`).
 *
 * REIN LESEND (siehe `drawerRechte.ts#DRAWER_TAB_EDIT_PERMISSIONS`, das fuer `history` deshalb
 * ausdruecklich `undefined` traegt): jedes Bedienelement dieses Reiters - Sortierknopf, Spaltenfilter,
 * Freitextsuche, Aufklapp-Schalter - veraendert nur die CLIENTSEITIGE Ansicht der bereits geladenen
 * Liste, keines schreibt etwas zur Bestellung. Der Reiter laedt seine Liste selbst (wie `TasksTab`
 * seine Zustaendigkeitsmodule), statt sie von `App.tsx` durchgereicht zu bekommen - die Historie ist
 * reine Detailansicht, kein Store-Feld.
 *
 * ================= `summary == null`/leer - zwei dokumentierte Backend-Luecken =================
 *
 * `HistorienEintrag.summary` ist im API-Typ `string`, LAUFZEIT aber fuer Statuswechsel ohne Notiz
 * `null` (siehe Kopfkommentar `api/historie.ts`). Ein roher `{e.summary}` wuerde React dazu bringen,
 * den literalen Text "null" zu zeichnen (JSX rendert `null`/`undefined` selbst nicht, aber
 * `String(null)` als Teil einer Zeichenkette schon) - `zeigeSummary()` (`components/
 * historieDarstellung.ts`) faengt das ab und zeigt stattdessen einen neutralen Platzhalter.
 * Nachbesserung (Auftrag "Historie-Eintraege bereinigen", BE-10486, 2026-09-07): `zeigeSummary()`
 * behandelt zusaetzlich einen leeren/nur-Leerzeichen-String gleich - `migration/verlauf.ts` schreibt
 * bei fehlendem Alt-Wert `summary: ""` statt `null` (order_audit_log.summary ist `NOT NULL`), ein
 * reines `summary ?? KEINE_BESCHREIBUNG` liess diesen Fall unveraendert durch und zeigte eine LEERE
 * Titelzeile statt des Platzhaltertexts.
 *
 * ================= Nachbesserung (Pruefer-Befunde, 2026-09-05) =================
 *
 * 1. `betroffenerText()`: `betroffene`-Eintraege tragen je nach Ereignistyp eine ANDERE Form - Task-
 *    Sammelereignisse `{name: "..."}` (`backend/src/domaene/tasks.ts`), Positions-Sammelereignisse
 *    dagegen `{jtlPosId: 12345}` OHNE `name` (`backend/src/domaene/items.ts`). Ein Zugriff auf `name`
 *    allein zeigte fuer Letztere ein nacktes "?". Bekannte Formen bekommen jetzt einen sprechenden
 *    Rueckfall, eine wirklich unbekannte Form zeigt ihren rohen Inhalt statt eines Platzhalters ohne
 *    Aussagekraft. Seit Phase 2 (Ruling 32) tragen Zeilen auf Ratenebene `{rateId, position, label}`
 *    (`backend/src/domaene/ratenbezug.ts`); sie zeigen "Rate <n> (<Bezeichnung>)", nie die Rate-Id.
 *    Phase 2, Task 11B: weitere Formen tragen ihren Namen statt rohem JSON - ein Dokument seinen Dateinamen
 *    (`originalName`), eine Bestellgruppe die Zahl ihrer Bestellungen (`{ groupId, orderIds }`). Das Backend liefert
 *    Bezeichnungen; eine rohe UUID in `betroffene` ist ein Fehler seines Schreibwegs (z. B. `kindKennung`, Task 12) und wird
 *    hier nicht ueberall abgefangen: der Rueckfall auf den rohen Inhalt laesst UUID-Werte weg, `{ name }` zeigt den Namen,
 *    wie er kommt.
 * 2. Stilregeln: die aufklappbaren Sammel-Details tragen zwei eigene, sinnvoll benannte Klassen
 *    (`history-tab-expand`/`history-tab-details`), samt eigener, minimaler Stilregel in
 *    `styles/neubau/historieReiter.css`.
 *
 * ================= Zweite Nachbesserung (Pruefer-Befunde, 2026-09-05) =================
 *
 * 1. Die zwei Stilregeln aus Punkt 2 oben standen zunaechst DIREKT in `bestand.css` - die Datei muss
 *    laut `test/reference/stylesheet.test.ts` aber ZEICHENGLEICH zum `<style>`-Block aus `index.html`
 *    bleiben, `gen-stylesheet.ts` haette sie beim naechsten Lauf stillschweigend wieder entfernt.
 *    Jetzt wie jedes andere neue Reiter-CSS in einer eigenen Datei unter `styles/neubau/`
 *    (`historieReiter.css`), eingebunden in `main.tsx` - siehe deren Kopfkommentar.
 * 2. `data-permission="view_board"` am aeusseren Container: `DRAWER_TAB_EDIT_PERMISSIONS.history` ist
 *    `undefined` (siehe `drawerRechte.ts`) - ohne dieses Attribut sperrt der generische
 *    Rechte-Durchgang des Rahmens (`Drawer.tsx`, `control.closest('[data-permission]')`) JEDES
 *    Bedienelement dieses Reiters fuer JEDE Rolle, obwohl keines davon schreibt - Constraint 9
 *    ("Lesen steht allen Rollen offen"). Attribut am AEUSSEREN Container statt an jedem einzelnen
 *    Schalter: dieselbe Begruendung wie bei `ArrivalTab/Anlieferungsliste.tsx#data-permission=
 *    "view_board"` an deren `<table>` - EIN Attribut deckt automatisch JEDEN Sortierknopf, jedes
 *    Filtermenue und jeden Aufklapp-Schalter ab. `view_board` ist dasselbe Recht wie dort - eines der
 *    fuenf Leserechte, das bei JEDER Systemrolle steht.
 *
 * ================= Dritte Nachbesserung (Task-13-Pruefer-Befund "Verdopplung", 2026-09-05) =======
 *
 * `KEINE_BESCHREIBUNG` und `iconFuer` standen hier wortgleich zu ihren Entsprechungen in
 * `OverviewPage/Historie.tsx` - beide Reiter zeichnen dieselben vier `kategorie`-Werte mit demselben
 * Symbol/derselben Farbklasse. Extrahiert nach `components/historieDarstellung.ts`, von dort
 * importiert statt lokal definiert. `betroffenerText()` (Nachbesserungspunkt 1 oben) bleibt hier -
 * das ist eine reine `HistoryTab`-Eigenheit ohne Entsprechung in der Kachel.
 *
 * ================= Auftrag "Historie-Tab als sortier-/filterbare Tabelle" (2026-09-06) ============
 *
 * Umbau von der reinen Kartenliste (Icon + Text + Zeitstempel je Zeile) zu einer `<table>`, deren
 * Bedienung optisch/funktional an die Bestellübersicht (`components/Board/`) angelehnt ist - dieselben
 * Bausteine wiederverwendet, wo sie schon generisch genug sind, statt neu erfunden:
 *
 * - `MehrfachauswahlFilter`/`DatumsBereichFilter` (`components/Board/filter/`) DIREKT importiert, kein
 *   eigener Nachbau.
 * - Die CSS-Klassen `order-list-th`/`order-list-sort-btn`/`order-list-sort`/`order-list-filter`/
 *   `order-list-filter-menu`/`spaltenfilter-*` sind bereits GLOBAL definiert (`styles/bestand.css`,
 *   `styles/neubau/spaltenfilter.css`) - fuer Sortierknopf und Filtermenue unveraendert wiederverwendet.
 * - `Spaltenkopf.tsx` (Board) ist NUR das Vorbild fuer das Zusammenspiel Sortierknopf + `<details>`-
 *   Filtermenue + Klick-aussen-Schliessen + `hidden` bei geschlossenem `<details>` - NACHGEBAUT im
 *   lokalen `HistorySpaltenkopf` unten, OHNE dessen breites Prop-Geflecht (Breiten, `facetOrders`,
 *   `definitionen`, `lieferanten` - reine Board/Order-Kopplung) und OHNE dessen spaltenuebergreifende
 *   "nur ein Menue gleichzeitig offen"-Koordination (die sitzt dort eine Ebene hoeher, in
 *   `BoardSeite.tsx` - bei genau drei filterbaren Spalten hier nicht noetig): jede Spalte verwaltet
 *   ihr eigenes `offen`, klick-aussen schliesst NUR ihr eigenes Menue.
 * - `Board/Werkzeugleiste.tsx` ist NUR das OPTISCHE Vorbild fuer die schmale Werkzeugleiste (Suchfeld +
 *   "Filter zuruecksetzen") - eigene, schlankere CSS-Klassen in `historieReiter.css` statt Reuse der
 *   Board-spezifischen `order-list-toolbar`/`table.order-list` (die tragen Annahmen wie
 *   Spalten-Resize, die hier nicht gelten).
 * - NICHT uebernommen (YAGNI, nur wegen Order-spezifischer Spalten im Board): Spalten-Resize,
 *   Breiten-Persistenz, `BetragsBereichFilter`, `BooleanFilter`, das ganze `lib/orderList.ts`-Modul -
 *   das Pendant dazu ist das neue, deutlich kleinere `lib/historyList.ts`.
 *
 * Spalte 3 "Änderung" bleibt inhaltlich UNVERAENDERT (Fettext `zeigeAuditSummary(e)` + Aktor
 * + aufklappbare Details) - weder sortierbar noch mit eigenem Spaltenfilter. Der Aktor-Wert steht
 * dadurch ABSICHTLICH ZWEIMAL im DOM (einmal hier, einmal in der neuen, eigenstaendig sortier-/
 * filterbaren Spalte 4 "Aktor") - keine Dublette im Sinne von Code, sondern zwei fachlich
 * verschiedene Zellen (die eine ist reiner Anzeigetext, die andere ein bedienbarer Spaltenwert).
 *
 * Der Aufklapp-Zustand (`aufgeklappt`) bleibt weiterhin ueber den Index im ROHEN, unsortierten
 * `eintraege`-Array indiziert (nicht ueber die Position in der sortierten/gefilterten Anzeige) -
 * sonst spraenge eine bereits aufgeklappte Zeile beim Umsortieren auf einen anderen Eintrag um.
 *
 * Die Freitextsuche (`passtSuche`) bleibt hier lokal statt in `lib/historyList.ts` zu wandern - siehe
 * dessen Kopfkommentar (Layering-Grund: `KEINE_BESCHREIBUNG` kommt aus `components/`).
 *
 * ================= Nachbesserung (Pruefer-Befunde "Wichtig" 1+2, 2026-09-06) ====================
 *
 * 1. Der Zeitraum-Filter der Spalte "Zeitpunkt" verglich den Tag ueber `ts.slice(0, 10)` (UTC), die
 *    Zelle zeigt aber `fmtDT` in ORTSZEIT - beide konnten fuer Eintraege nahe der Tagesgrenze
 *    (22-24 Uhr UTC) verschiedene Tage ergeben. Der Vergleich laeuft jetzt ueber `localIsoDay`
 *    (`lib/format.ts`), siehe Kopfkommentar `lib/historyList.ts#historyZeilePasstFilter`.
 * 2. Die Checkbox "Nur ohne Wert", die `DatumsBereichFilter` sonst UNGEFRAGT mitliefert, konnte bei
 *    "Zeitpunkt" NIE einen Treffer liefern (`ts` ist nie leer) - ein Klick darauf leerte zuverlaessig
 *    die ganze Tabelle. `DatumsBereichFilter` bekam dafuer ein optionales `ohneWertVerstecken`-Prop
 *    (Default `false`, der Board-Aufrufer `Spaltenkopf.tsx` bleibt dadurch unveraendert), hier fuer
 *    die Zeitpunkt-Spalte gesetzt.
 *
 * ================= Ladefehler (Fehleranzeige Paket 2, Task 8, Regel L1) ==========================
 *
 * Scheitert das Laden, steht an der Flaeche des Reiters „Die Historie konnte nicht geladen werden." samt
 * Ursache und „Erneut laden" (`Ladefehler/Ladefehler.tsx`) - NICHT der Leertext „Noch keine Änderungen
 * erfasst.", der nur noch heisst: geladen, und es gab nichts. Die Flaeche meldet nicht in den Fehlerstapel.
 * Ein Abbruch (Bestellungs-/Reiterwechsel, Schliessen) und ein 401 erzeugen keine Flaeche (`fehlertext` →
 * `null`, die Komponente rendert dann nichts). „Erneut laden" laedt die AKTUELLE Bestellung (`orderId` des
 * Rendern, nicht die des ersten Fehlschlags) und liefert ein Promise, das nie abgelehnt wird und erst nach dem
 * Laden erfuellt ist; `key={orderId}` an der Flaeche haelt die Sperre des Knopfes je Bestellung getrennt.
 */
import { useEffect, useRef, useState } from "react";
import { historieLaden } from "../../../api/historie.ts";
import type { HistorienEintrag } from "../../../api/historie.ts";
import { UUID_MUSTER } from "../../App/bestellungsAufloesung.ts";
import { iconFuer, zeigeAuditSummary, zeigeSummary } from "../../historieDarstellung.ts";
import { fmtDT, normSearch } from "../../../lib/format.ts";
import { Ikone } from "../../Ikone.tsx";
import { Ladefehler } from "../../Ladefehler/Ladefehler.tsx";
import { MehrfachauswahlFilter } from "../../Board/filter/MehrfachauswahlFilter.tsx";
import { DatumsBereichFilter } from "../../Board/filter/DatumsBereichFilter.tsx";
import {
  historyAktorOptionen,
  historyFilterAktiv,
  historyKategorieOptionen,
  historySortValue,
  historyZeilePasstFilter,
  zeitraumFilterAktiv,
  HISTORY_KATEGORIE_LABEL,
} from "../../../lib/historyList.ts";
import type {
  Bereichsfilter,
  HistoryFilter,
  HistorySpalte,
  HistorySortierung,
} from "../../../lib/historyList.ts";

export interface HistoryTabProps {
  orderId: string;
}

/** `JSON.stringify`-Ersetzer des Rueckfalls: UUID-Werte (auch in Listen) fallen weg - eine Kennung sagt dem Leser nichts. */
function ohneUuid(_schluessel: string, wert: unknown): unknown {
  if (typeof wert === "string" && UUID_MUSTER.test(wert)) return undefined;
  return Array.isArray(wert) ? wert.filter((e) => !(typeof e === "string" && UUID_MUSTER.test(e))) : wert;
}

/**
 * Siehe Kopfkommentar, Nachbesserungspunkt 1. Dazu die Form der Ratenzeilen (Phase 2, Ruling 32;
 * `backend/src/domaene/ratenbezug.ts`): `{ rateId, position, label }` zeigt "Rate <position> (<label>)", ohne
 * Bezeichnung "Rate <position>" - die Rate-Id (eine UUID) erscheint nie. Die Formen `{ name }` und `{ jtlPosId }`
 * bleiben unveraendert, ebenso aeltere Zeilen im Altformat.
 */
function betroffenerText(b: {
  name?: string;
  jtlPosId?: unknown;
  rateId?: unknown;
  position?: unknown;
  label?: unknown;
  [k: string]: unknown;
}): string {
  if (typeof b.name === "string") return b.name;
  if (b.rateId != null) {
    const rate = b.position != null ? `Rate ${String(b.position)}` : "Rate";
    return typeof b.label === "string" && b.label.trim() !== "" ? `${rate} (${b.label.trim()})` : rate;
  }
  if (b.jtlPosId != null) return `Position ${String(b.jtlPosId)}`;
  if (typeof b.originalName === "string" && b.originalName.trim() !== "") return b.originalName;
  if (typeof b.groupId === "string") {
    const anzahl = Array.isArray(b.orderIds) ? b.orderIds.length : 0;
    if (anzahl === 0) return "Bestellgruppe";
    return anzahl === 1 ? "Bestellgruppe mit 1 Bestellung" : `Bestellgruppe mit ${anzahl} Bestellungen`;
  }
  return JSON.stringify(b, ohneUuid);
}

/** Der Wert als Suchmuster: Sonderzeichen woertlich. */
const alsMuster = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * M7 (Phase 2, Task 4/11B): Details gibt es nur, wenn sie etwas sagen, was die Zeile nicht schon sagt. Ein einziger
 * `betroffene`-Eintrag, dessen Text als eigenes Wort schon in der Zusammenfassung steht (der Titel einer Aufgabe, "Rate 2
 * (Anzahlung)", "Position 12345"), bekaeme sonst an jeder Zeile den Knopf "1 Details anzeigen" und dahinter nur eine
 * Wiederholung. Mehrere Eintraege behalten ihn - sie zaehlen auf. Die Regel gilt fuer jede Form, nicht nur fuer Aufgaben.
 */
function hatDetails(eintrag: HistorienEintrag): boolean {
  const liste = eintrag.betroffene;
  if (!liste || liste.length === 0) return false;
  if (liste.length > 1) return true;
  const text = betroffenerText(liste[0]!).trim();
  if (text === "") return true;
  const wiederholt = new RegExp(`(?<![\\p{L}\\p{N}_])${alsMuster(text)}(?![\\p{L}\\p{N}_])`, "iu");
  return !wiederholt.test(zeigeAuditSummary(eintrag));
}

/**
 * Die Freitextsuche der Werkzeugleiste - durchsucht `summary`/`feld`/`aktor`, case-insensitiv. Die
 * Rueckfaelle (`zeigeSummary()`/`"System"`) werden VOR dem Vergleich angewandt: ein Suchbegriff
 * wie "system" muesste sonst einen Eintrag mit `aktor: null` verpassen, obwohl genau der Text
 * "System" sichtbar in seiner Zeile steht (siehe Auftrag). Nachbesserung (Pruefer-Befund, BE-10486):
 * ein rohes `summary ?? KEINE_BESCHREIBUNG` faengt nur `null`/`undefined` ab, nicht den Leerstring
 * aus `migration/verlauf.ts` - eine solche Zeile zeigte sichtbar "(keine Beschreibung)", war aber
 * ueber genau diesen Suchbegriff nicht auffindbar. `zeigeSummary()` deckt beide Faelle einheitlich
 * ab, wie schon beim Rendern selbst (siehe `<strong>{zeigeAuditSummary(e)}</strong>` unten).
 */
function passtSuche(eintrag: HistorienEintrag, suche: string): boolean {
  const bedarf = normSearch(suche);
  if (!bedarf) return true;
  const felder = [zeigeSummary(eintrag.summary), eintrag.feld ?? "", eintrag.aktor ?? "System"];
  return felder.some((feld) => normSearch(feld).includes(bedarf));
}

const SPALTEN_LABEL: Record<HistorySpalte, string> = {
  ts: "Zeitpunkt",
  kategorie: "Kategorie",
  aktor: "Aktor",
};

/**
 * Ein sortier-/filterbarer Spaltenkopf - NACHGEBAUT nach `Board/Spaltenkopf.tsx` (siehe Kopfkommentar
 * dieser Datei), aber ohne dessen Board/Order-Kopplung und ohne spaltenuebergreifende Koordination:
 * `offen` ist lokaler State, nicht von aussen gesteuert - bei drei filterbaren Spalten kein Bedarf,
 * das ganze Prop-Geflecht fuer "nur ein Menue gleichzeitig offen" nachzubauen.
 */
function HistorySpaltenkopf({
  spalte,
  sortierung,
  onSortieren,
  aktiv,
  onFilterLeeren,
  children,
}: {
  spalte: HistorySpalte;
  sortierung: HistorySortierung;
  onSortieren: (spalte: HistorySpalte) => void;
  aktiv: boolean;
  onFilterLeeren: () => void;
  children: React.ReactNode;
}) {
  const label = SPALTEN_LABEL[spalte];
  const [offen, setOffen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const sortAktiv = sortierung.key === spalte;
  const sortIcon = sortAktiv
    ? sortierung.direction === "asc"
      ? "arrow-up"
      : "arrow-down"
    : "arrow-up-down";

  // Klick ausserhalb des offenen Menues schliesst es - dieselbe Capture-Phase-Absicherung wie
  // `Spaltenkopf.tsx` (siehe dessen Kopfkommentar, Eigenheit 6): ohne Capture-Phase koennte ein Klick
  // auf eine Zeile, die selbst `stopPropagation()` ruft, den Listener nie erreichen.
  useEffect(() => {
    if (!offen) return;
    const aufAussenKlick = (ereignis: MouseEvent): void => {
      if (detailsRef.current?.contains(ereignis.target as Node)) return;
      setOffen(false);
    };
    document.addEventListener("click", aufAussenKlick, true);
    return () => document.removeEventListener("click", aufAussenKlick, true);
  }, [offen]);

  return (
    <th data-history-column={spalte}>
      <div className="order-list-th">
        <button
          className="order-list-sort-btn"
          type="button"
          onClick={() => onSortieren(spalte)}
          title={`Nach ${label} sortieren`}
        >
          <span>{label}</span>
          <span className="order-list-sort">
            <Ikone name={sortIcon} />
          </span>
        </button>
        <details
          ref={detailsRef}
          className={`order-list-filter ${aktiv ? "active" : ""}`}
          open={offen}
          onClick={(ereignis) => ereignis.stopPropagation()}
          onToggle={(ereignis) => setOffen((ereignis.target as HTMLDetailsElement).open)}
        >
          <summary title={`${label} filtern`} aria-label={`${label} filtern`}>
            <Ikone name="filter" />
          </summary>
          <div className="order-list-filter-menu" hidden={!offen}>
            <button
              type="button"
              className="spaltenfilter-schliessen"
              aria-label={`${label}-Filter schließen`}
              onClick={() => setOffen(false)}
            >
              <Ikone name="x" />
            </button>
            <label>{`${label} filtern`}</label>
            {children}
            {aktiv ? (
              <button type="button" onClick={onFilterLeeren}>
                Filter entfernen
              </button>
            ) : null}
          </div>
        </details>
      </div>
    </th>
  );
}

export function HistoryTab({ orderId }: HistoryTabProps) {
  const [eintraege, setEintraege] = useState<HistorienEintrag[]>([]);
  const [geladen, setGeladen] = useState(false);
  // Sitzungsweit KEIN Modul-Global wie `SEC_COLLAPSED` (`Abschnitt.tsx`): jede Zeile dieses Reiters
  // ist eine eigene Bestellung/eigener Eintrag, nicht ein wiederkehrender Abschnittsname - lokaler
  // State genuegt, jeder Reiterwechsel startet zugeklappt. Indiziert ueber den Index im ROHEN
  // `eintraege`-Array (siehe Kopfkommentar).
  const [aufgeklappt, setAufgeklappt] = useState<Set<number>>(new Set());

  const [sortierung, setSortierung] = useState<HistorySortierung>({ key: "ts", direction: "desc" });
  const [filterKategorie, setFilterKategorie] = useState<string[]>([]);
  const [filterAktor, setFilterAktor] = useState<string[]>([]);
  const [filterZeitraum, setFilterZeitraum] = useState<Bereichsfilter>({});
  const [suche, setSuche] = useState("");
  // Ladefehler (Fehleranzeige Paket 2, Regel L1): der Fehler selbst, eingewickelt, weil auch `undefined` ein
  // geworfener Wert sein kann. `null` heisst: kein Fehler.
  const [ladefehler, setLadefehler] = useState<{ ursache: unknown } | null>(null);
  // Der laufende Abruf - Effekt UND „Erneut laden" teilen ihn: ein neuer Lauf bricht den vorigen ab, und nur der
  // jeweils letzte darf den Zustand schreiben (sonst gewaenne eine ueberholte Antwort der Bestellung A in B).
  const lauf = useRef<AbortController | null>(null);

  /**
   * Laedt die Historie von `id`. Liefert ein Promise, das IMMER erfuellt wird (`<Ladefehler>` faengt kein
   * abgelehntes Promise; ein solches ginge an den globalen Auffaenger und meldete in den Stapel - Flaeche UND
   * Eintrag). Es ist erst nach dem Laden erfuellt: die Flaeche bleibt waehrend des Neuversuchs im Baum, ihr Knopf
   * ist bis dahin gesperrt. Ein ueberholter oder abgebrochener Lauf (Bestellungs- oder Reiterwechsel, Schliessen)
   * schreibt nichts: weder eine Flaeche noch einen Eintrag (L5).
   */
  async function historieHolen(id: string): Promise<void> {
    lauf.current?.abort();
    const controller = new AbortController();
    lauf.current = controller;
    try {
      const r = await historieLaden(id, controller.signal);
      if (controller.signal.aborted) return;
      setEintraege(r);
      setLadefehler(null);
      setGeladen(true);
    } catch (ursache) {
      if (controller.signal.aborted) return;
      setLadefehler({ ursache });
      setGeladen(true);
    }
  }

  useEffect(() => {
    setGeladen(false);
    setLadefehler(null);
    setAufgeklappt(new Set());
    void historieHolen(orderId);
    return () => lauf.current?.abort();
    // `historieHolen` haengt nur an Setzern und an `lauf` - die Bestellung ist der einzige Ausloeser.
  }, [orderId]);

  if (!geladen) return <div className="history-tab-loading">Lädt…</div>;
  if (ladefehler !== null) {
    // `data-permission="view_board"`: lesender Neuversuch, der Rechte-Durchgang des Rahmens (`Drawer.tsx`) soll
    // den Knopf nicht fuer jede Rolle sperren (siehe Kopfkommentar, zweite Nachbesserung, Punkt 2).
    // `key` auf die Bestellung: dieselbe Flaeche steht nacheinander fuer wechselnde Bestellungen, und der Knopf
    // darf fuer B nicht gesperrt bleiben, solange der Neuversuch fuer A noch laeuft.
    return (
      <div data-permission="view_board">
        <Ladefehler
          key={orderId}
          was="Die Historie konnte nicht geladen werden."
          ursache={ladefehler.ursache}
          onErneutLaden={() => historieHolen(orderId)}
        />
      </div>
    );
  }
  if (eintraege.length === 0) {
    return <div className="ops-empty">Noch keine Änderungen erfasst.</div>;
  }

  const spaltenFilter: HistoryFilter = {
    kategorie: filterKategorie,
    aktor: filterAktor,
    zeitraum: filterZeitraum,
  };
  const spaltenFilterAktiv = historyFilterAktiv(spaltenFilter);
  const sucheAktiv = suche.trim() !== "";

  const sichtbar = eintraege
    .map((eintrag, index) => ({ eintrag, index }))
    .filter(
      ({ eintrag }) => historyZeilePasstFilter(eintrag, spaltenFilter) && passtSuche(eintrag, suche),
    );
  const richtung = sortierung.direction === "asc" ? 1 : -1;
  sichtbar.sort(
    (a, b) =>
      historySortValue(a.eintrag, sortierung.key).localeCompare(
        historySortValue(b.eintrag, sortierung.key),
        "de",
        { numeric: true, sensitivity: "base" },
      ) * richtung,
  );

  const sortieren = (spalte: HistorySpalte): void => {
    setSortierung((prev) =>
      prev.key === spalte
        ? { key: spalte, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key: spalte, direction: spalte === "ts" ? "desc" : "asc" },
    );
  };

  const filterZuruecksetzen = (): void => {
    setSuche("");
    setFilterKategorie([]);
    setFilterAktor([]);
    setFilterZeitraum({});
  };

  return (
    <div className="history-table-shell" data-permission="view_board">
      <div className="history-table-toolbar">
        <input
          type="text"
          value={suche}
          onChange={(ereignis) => setSuche(ereignis.target.value)}
          placeholder="Änderung, Feld oder Aktor suchen"
          aria-label="Historie durchsuchen"
        />
        {sucheAktiv || spaltenFilterAktiv ? (
          <button
            type="button"
            className="history-table-reset icon-label"
            onClick={filterZuruecksetzen}
          >
            <Ikone name="filter-x" />
            Filter zurücksetzen
          </button>
        ) : null}
      </div>
      {sichtbar.length === 0 ? (
        <div className="ops-empty">Keine Einträge für diese Filter.</div>
      ) : (
        <div className="history-table-scroll">
          <table className="history-table">
            <thead>
              <tr>
                <HistorySpaltenkopf
                  spalte="ts"
                  sortierung={sortierung}
                  onSortieren={sortieren}
                  aktiv={zeitraumFilterAktiv(filterZeitraum)}
                  onFilterLeeren={() => setFilterZeitraum({})}
                >
                  <DatumsBereichFilter
                    label="Zeitpunkt"
                    bereich={filterZeitraum}
                    onAendern={setFilterZeitraum}
                    ohneWertVerstecken
                  />
                </HistorySpaltenkopf>
                <HistorySpaltenkopf
                  spalte="kategorie"
                  sortierung={sortierung}
                  onSortieren={sortieren}
                  aktiv={filterKategorie.length > 0}
                  onFilterLeeren={() => setFilterKategorie([])}
                >
                  <MehrfachauswahlFilter
                    label="Kategorie"
                    optionen={historyKategorieOptionen()}
                    ausgewaehlt={filterKategorie}
                    onAendern={setFilterKategorie}
                  />
                </HistorySpaltenkopf>
                <th>Änderung</th>
                <HistorySpaltenkopf
                  spalte="aktor"
                  sortierung={sortierung}
                  onSortieren={sortieren}
                  aktiv={filterAktor.length > 0}
                  onFilterLeeren={() => setFilterAktor([])}
                >
                  <MehrfachauswahlFilter
                    label="Aktor"
                    optionen={historyAktorOptionen(eintraege)}
                    ausgewaehlt={filterAktor}
                    onAendern={setFilterAktor}
                  />
                </HistorySpaltenkopf>
              </tr>
            </thead>
            <tbody>
              {sichtbar.map(({ eintrag: e, index: i }) => {
                const mitDetails = hatDetails(e);
                const { symbol, klasse } = iconFuer(e.kategorie);
                return (
                  <tr key={i}>
                    <td>
                      <span className="ops-history-time">{fmtDT(e.ts)}</span>
                    </td>
                    <td>
                      <span className="history-table-kategorie">
                        <span className={klasse ? `ops-history-icon ${klasse}` : "ops-history-icon"}>
                          <Ikone name={symbol} />
                        </span>
                        {HISTORY_KATEGORIE_LABEL[e.kategorie]}
                      </span>
                    </td>
                    <td>
                      <div className="ops-history-copy">
                        <strong>{zeigeAuditSummary(e)}</strong>
                        <span>{e.aktor ?? "System"}</span>
                        {mitDetails && (
                          <button
                            type="button"
                            className="history-tab-expand"
                            onClick={() =>
                              setAufgeklappt((s) => {
                                const next = new Set(s);
                                if (next.has(i)) {
                                  next.delete(i);
                                } else {
                                  next.add(i);
                                }
                                return next;
                              })
                            }
                          >
                            {aufgeklappt.has(i)
                              ? "Details ausblenden"
                              : `${e.betroffene!.length} Details anzeigen`}
                          </button>
                        )}
                        {aufgeklappt.has(i) && mitDetails && (
                          <ul className="history-tab-details">
                            {e.betroffene!.map((b, j) => <li key={j}>{betroffenerText(b)}</li>)}
                          </ul>
                        )}
                      </div>
                    </td>
                    <td>{e.aktor ?? "System"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
